import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play, Star } from "lucide-react";
import { MaskedLines } from "@/lib/motion";
import { useConsult } from "@/context/ConsultContext";

const HERO_IMG = "https://images.pexels.com/photos/13062586/pexels-photo-13062586.jpeg?auto=compress&cs=tinysrgb&w=1200";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const yBadge = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const { openConsult } = useConsult();

  const scrollToServices = () => document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden" data-testid="hero-section">
      {/* soft ambient blobs */}
      <div className="absolute -top-32 -left-32 w-[32rem] h-[32rem] rounded-full opacity-[0.12] blur-3xl pointer-events-none" style={{ background: "#6C2BD9" }} />
      <div className="absolute top-40 -right-24 w-96 h-96 rounded-full opacity-[0.10] blur-3xl pointer-events-none" style={{ background: "#F97316" }} />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-8 items-center">
        {/* left */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-1.5 mb-7 shadow-sm"
          >
            <span className="flex -space-x-1">
              {[0, 1, 2].map((i) => <Star key={i} size={13} className="fill-[var(--dm-orange)] text-[var(--dm-orange)]" />)}
            </span>
            <span className="overline text-slate-600">Data-Driven Marketing Agency</span>
          </motion.div>

          <h1 className="font-head font-extrabold tracking-tighter leading-[0.98] text-slate-900 text-4xl sm:text-5xl lg:text-[3.4rem]">
            <MaskedLines
              lines={["Boost Your", "Business Using"]}
              delay={0.15}
            />
            <span className="reveal-line">
              <motion.span
                className="block text-gradient"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.15 + 2 * 0.12, ease: [0.22, 1, 0.36, 1] }}
              >
                Smart Online Marketing
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-7 text-lg text-slate-600 leading-relaxed max-w-xl"
          >
            We help brands increase visibility, generate qualified leads, and boost revenue through data-driven digital marketing strategies.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={openConsult}
              data-testid="hero-primary-cta"
              className="group inline-flex items-center gap-2 rounded-full px-7 py-4 font-head font-bold text-white transition-transform duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-purple-300"
              style={{ background: "linear-gradient(135deg,#6C2BD9,#2563EB)" }}
            >
              Book a Free Consultation
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={scrollToServices}
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 rounded-full px-7 py-4 font-head font-bold text-slate-900 border-2 border-slate-200 hover:border-[var(--dm-purple)] hover:text-[var(--dm-purple)] transition-colors duration-300"
            >
              <Play size={16} className="fill-current" />
              View Our Services
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.7 }}
            className="mt-12 flex items-center gap-8"
          >
            {[["250+", "Projects"], ["5.8x", "Avg ROAS"], ["98%", "Retention"]].map(([v, l]) => (
              <div key={l}>
                <div className="font-head font-extrabold text-2xl text-slate-900">{v}</div>
                <div className="text-sm text-slate-500">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* right — parallax graphic */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          <div className="relative rounded-[2rem] overflow-hidden shadow-2xl shadow-purple-200/60 border border-white">
            <motion.img
              src={HERO_IMG}
              alt="Abstract 3D marketing visualization"
              style={{ y: yImg, scale: scaleImg }}
              className="w-full h-[420px] lg:h-[520px] object-cover"
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(160deg, rgba(108,43,217,0.15), rgba(37,99,235,0.05))" }} />
          </div>

          {/* floating stat card */}
          <motion.div
            style={{ y: yBadge }}
            className="absolute -left-4 sm:-left-8 bottom-10 bg-white rounded-2xl shadow-xl border border-slate-100 px-5 py-4 w-56"
          >
            <div className="text-sm text-slate-500 mb-2 font-medium">Campaign Performance</div>
            <div className="flex items-end gap-1 h-14">
              {[35, 55, 42, 70, 60, 90, 100].map((h, i) => (
                <motion.div
                  key={i}
                  initial={{ height: 0 }}
                  animate={{ height: `${h}%` }}
                  transition={{ delay: 1 + i * 0.08, duration: 0.5 }}
                  className="flex-1 rounded-t-sm"
                  style={{ background: i > 4 ? "#F97316" : "linear-gradient(180deg,#6C2BD9,#2563EB)" }}
                />
              ))}
            </div>
            <div className="mt-2 text-xs font-bold text-green-600">▲ +312% conversions</div>
          </motion.div>

          {/* floating badge top right */}
          <motion.div
            style={{ y: useTransform(scrollYProgress, [0, 1], [0, 40]) }}
            className="absolute -right-2 sm:-right-6 top-8 bg-white rounded-2xl shadow-xl border border-slate-100 px-4 py-3 flex items-center gap-3"
          >
            <span className="w-9 h-9 rounded-full grid place-items-center text-white" style={{ background: "#F97316" }}>
              <Star size={16} className="fill-white" />
            </span>
            <div>
              <div className="font-head font-extrabold text-slate-900 leading-none">4.9/5</div>
              <div className="text-[11px] text-slate-500">Client Rating</div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
